import { BzDateTime } from '@benzinga/date-utils';
import { isNil, keys, prop, reduce, type } from 'ramda';

import { MarketSession } from '../entities/marketEntity';
import { formatNumberWithBigInt } from '@benzinga/utils';

// export interface RenameTransform<Before, After, BeforeKey extends keyof Before, AfterKey extends keyof After> {
//   after?: AfterKey;
//   transform(value: Before[BeforeKey]): After[AfterKey];
// }

// type RenameMap<Before, After> = {
//   [P in keyof Before]: keyof After | RenameTransform<Before, After, P, keyof After>;
// };

// const isRenameTransform = <Before, After>(
//   x: keyof After | RenameTransform<Before, After, keyof Before, keyof After> | undefined,
// ): x is RenameTransform<Before, After, keyof Before, keyof After> => !isNil(x) && type(x) !== 'String';

// export const renameFoundObjectProperties = <Before = {}, After = {}>(keysMap: RenameMap<Before, After>) => (
//   (original: Before) => (
//     reduce((acc: After, beforeKey) => {
//       const afterKey = prop(beforeKey, keysMap);
//       if (isNil(afterKey)) {
//         return acc;
//       }
//       const originalValue = prop(beforeKey, original);
//       if (!isRenameTransform<Before, After>(afterKey)) {
//         // @ts-ignore Typescript cannot statically determine that the After and Before have exactly the same value type for each respective key, because they will not necessarily line up.
//         acc[afterKey as keyof After] = originalValue;
//       } else {
//         const newKey = prop('after', afterKey);
//         const newValue = afterKey.transform(originalValue);
//         acc[newKey as keyof After] = newValue;
//       }
//       return acc;
//     },
//     {},
//     keys(original) as (keyof Before)[],
//   ))
// );

export const renameFoundObjectProperties =
  <Before, After>(keysMap) =>
  (original: Before) =>
    reduce(
      (acc, beforeKey) => {
        const afterKey = prop(beforeKey, keysMap);
        if (isNil(afterKey)) {
          return acc;
        }
        const originalValue = prop(beforeKey, original);
        if (type(afterKey) === 'String') {
          acc[afterKey] = originalValue;
        } else {
          const newKey = prop('after', afterKey);
          const newValue = afterKey.transform(originalValue);
          acc[newKey] = newValue;
        }
        return acc;
      },
      // tslint:disable-next-line:no-object-literal-type-assertion
      {} as After,
      keys(original) as (keyof Before)[],
    );

export function formatPhone(phoneNumber: string | number) {
  if (typeof phoneNumber === 'string') {
    const onlyNums = phoneNumber.replace(/\D/g, '');
    const separatedNums = onlyNums.match(/^(\d{3})(\d{3})(\d{4})$/);
    return separatedNums ? `(${separatedNums[1]}) ${separatedNums[2]}-${separatedNums[3]}` : phoneNumber;
  }
  return phoneNumber;
}

export function numberWithCommas(value: number) {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const roundNumber = (value: number, numberOfDecimalPlaces: number) =>
  Math.round(value * Math.pow(10, numberOfDecimalPlaces)) / Math.pow(10, numberOfDecimalPlaces);

// FIXME: not reporting properly?
export function numberShorthand(value: string | number | null | undefined, numberOfDecimalPlaces = 3): string {
  if (isNil(value)) {
    return '';
  }

  const stringValue = typeof value === 'number' ? value.toString() : value;

  if (stringValue.length === 0) {
    return stringValue;
  }

  const regEx = /^([+-]{1})?((?:\d+)?(?:\.\d+)?)$/;
  const matches = regEx.exec(stringValue);

  // Dunno how this should be handled, actually.
  if (matches === null) {
    return stringValue;
  }

  let sign = matches[1];
  let num = Number.parseFloat(matches[2]);

  if (!sign) {
    sign = '';
  }

  if (!num) {
    return '0';
  }

  const numSignificantDigits = String(Math.round(num)).length;
  let signifier = '';
  let divisor = 1;

  if (numSignificantDigits >= 4 && numSignificantDigits < 7) {
    divisor = 1000;
    signifier = 'K';
  } else if (numSignificantDigits >= 7 && numSignificantDigits < 10) {
    divisor = 1000000;
    signifier = 'M';
  } else if (numSignificantDigits >= 10 && numSignificantDigits < 13) {
    divisor = 1000000000;
    signifier = 'B';
  } else if (numSignificantDigits >= 13) {
    divisor = 1000000000000;
    signifier = 'T';
  }

  num /= divisor;

  const roundedNumber = roundNumber(num, numberOfDecimalPlaces);

  return `${sign}${roundedNumber.toFixed(numberOfDecimalPlaces)}${signifier}`;
}

export function numberPercent(value?: string | number | null, isFraction = true) {
  if (isNil(value)) {
    return null;
  }

  if (typeof value === 'number') {
    return `${(value * (isFraction ? 100 : 1)).toFixed(2)}%`;
  }

  if (value.length === 0) {
    return value;
  }

  const num = Number.parseFloat(value);

  if (Number.isNaN(num)) {
    return null;
  }

  const numFraction = num * (isFraction ? 100 : 1);

  return `${numFraction.toFixed(2)}%`;
}

export function numberInMillions(value) {
  if (!value || value.length === 0) {
    return value;
  }
  const shortened = value / 1000000;
  return shortened.toLocaleString();
}

export function numberOfDecimalPlaces(value) {
  // fastest method according to jsperf.com
  if (Math.floor(value) === value) {
    return 0;
  }
  return value.toString().split('.')[1].length || 0;
}

export function formatPrice(input) {
  // filters out non-numbers, +/-Infinity, and NaN, undefined, null
  if (!Number.isFinite(input)) {
    return '';
  }

  // 0 returns `0` with no deimal places
  if (input === 0) {
    return '0';
  }

  // return `~0` for numbers near zero
  if (input > -0.0001 && input < 0.0001) {
    return '~0';
  }

  const places = numberOfDecimalPlaces(input);
  if (places > 0) {
    // maximum of 4 decimal places
    if (places > 4) {
      return input.toFixed(4);
    }
    return input.toFixed(places);
  }

  // 0 decimal places, also could use `Number.isInteger(input)`
  if (places === 0) {
    return `${input}.0`;
  }

  // if our tests are correct, this return should be unreachable
  // but just in case
  return input.toString();
}

export function yesOrNo(data, fullWord) {
  if (data === true || data === 'Y' || data === 1) {
    return fullWord ? 'Yes' : 'Y';
  } else if (data === false || data === 'N' || data === 0) {
    return fullWord ? 'No' : 'N';
  }
  return null;
}

export function surprisePercent(estimated: string, actual: string) {
  const estimatedNumber = Number(estimated);
  const actualNumber = Number(actual);

  if (!(Number.isNaN(estimatedNumber) && Number.isNaN(actualNumber)) && estimatedNumber !== 0) {
    return 100 * ((actualNumber - estimatedNumber) / Math.abs(estimatedNumber));
  }

  return '\u00a0';
}

export function roundPercent(value) {
  const rounded = Math.round(value * 100) / 100;
  if (rounded) {
    return `${rounded.toLocaleString()}%`;
  }
  return '';
}

export interface CustomTimeLabels {
  afterMarket?: string;
  invalid?: string | null;
  preMarket?: string;
  regular?: string;
}

export function marketTime(time, custom: CustomTimeLabels = {}) {
  const preMarket = BzDateTime.fromTime('09:30:00');
  const afterMarket = BzDateTime.fromTime('16:00:00');
  const thisMoment = BzDateTime.fromTime(time);

  if (thisMoment.isBefore(preMarket)) {
    return custom.preMarket || MarketSession.preMarket;
  } else if (thisMoment.equalsOrAfter(afterMarket)) {
    return custom.afterMarket || MarketSession.afterHours;
  } else if (thisMoment.isBefore(afterMarket) && thisMoment.equalsOrAfter(preMarket)) {
    return custom.regular || MarketSession.regular;
  } else if (Object.prototype.hasOwnProperty.call(custom, 'invalid')) {
    return custom.invalid;
  }
  return time;
}

const formatPercentage = (n: number): string => {
  const changeAbs = Math.abs(n);

  if (changeAbs >= 10) return n.toFixed(1);
  if (changeAbs > 0) return n.toFixed(2);
  if (n === 0) return '0.00';

  return n.toString();
};

export const getPerformanceDisplay = (
  label: string | undefined,
  stat: number | null | undefined,
  isPercentage: boolean | undefined,
  suppressPlusSign: boolean | undefined,
  decimalPlaces: number | undefined,
) => {
  let display = '';

  if (isNil(label)) {
    const n = Number(stat);
    if (isPercentage) {
      display = formatPercentage(n);
    } else {
      if (decimalPlaces) {
        display = formatNumberWithBigInt(n, decimalPlaces);
      } else {
        display = `${n}`;
      }
    }

    if (!suppressPlusSign) {
      if (Math.sign(n) === 1) {
        display = `+${display}`;
      }
    }

    if (isPercentage) {
      display = `[${display}%]`;
    }
  } else {
    display = label;
  }

  return display;
};
